// src/auth/scheduleTokenRefresh.ts
import {getStoredRefreshToken, getStoredToken, storeTokens, clearTokens, getTokenExpiryTime, tokensRotatedSince} from './auth';
import {api} from '../api/apiService';

let refreshTimeout: NodeJS.Timeout | null = null;

export const scheduleTokenRefresh = (expiresIn: number, onSessionExpired: () => void) => {
  if (refreshTimeout) clearTimeout(refreshTimeout);

  refreshTimeout = setTimeout(async () => {
    const refreshToken = getStoredRefreshToken();
    try {
      if (!refreshToken) throw new Error('No refresh token available');

      // Send request to refresh the token
      const response = await api.post(
        '/api/auth/refresh',
        {refresh_token: refreshToken},
        {skipAuth: true} // Ensure this request doesn't use the expired access token
      );

      const newAccessToken = response.access_token;
      const newRefreshToken = response.refresh_token || refreshToken;

      // Store the new tokens
      storeTokens(newAccessToken, newRefreshToken);

      // Get the expiry time from the new access token
      const newExpiryTime = getTokenExpiryTime(newAccessToken);
      if (newExpiryTime) {
        const timeUntilExpiry = newExpiryTime - Date.now();
        scheduleTokenRefresh(timeUntilExpiry / 1000, onSessionExpired); // Reschedule for next expiry
      }
    } catch (error) {
      // New tokens were stored meanwhile (e.g. a password change revoked the old session and
      // returned a fresh pair): keep them and schedule from their expiry instead of signing out.
      if (tokensRotatedSince(refreshToken)) {
        const expiry = getTokenExpiryTime(getStoredToken());
        if (expiry) scheduleTokenRefresh((expiry - Date.now()) / 1000, onSessionExpired);
        return;
      }
      console.error('Token refresh failed:', error);
      // Session is over. Drop the cached user too: a reload would otherwise restore it
      // from localStorage and the next sign-in would start as the previous user.
      clearTokens();
      onSessionExpired(); // Trigger the callback explicitly on failure
    }
  }, expiresIn * 1000 - 5000); // Refresh 5 seconds before expiry
};